/**
 * Copy of the provision screens (ALC-4476, S-4/S-5/S-6): the open provisions list, the aging
 * report, the close-out and the heal dialogs, the entry log, and the provision section on an
 * invoice and a contract. Spread into the `billing` namespace as the top-level `costProvisions`
 * block.
 *
 * Error sentences are NOT here: they live once in `COST_SERVICE_ERROR_MESSAGES` and are rendered
 * from the stable `code`. The reasons a provision is waiting to post live once in
 * `COST_RECOGNITION_PENDING_REASON_MESSAGES`.
 */
export const costProvisionsPtBr = {
  costProvisions: {
    title: 'Custos provisionados',
    subtitle:
      'O que você já reconheceu de custo junto com a receita e o fornecedor ainda não cobrou. Cada baixa por conta a pagar reduz o saldo em aberto.',
    nav: {
      open: 'Em aberto',
      aging: 'Envelhecimento',
    },
    status: {
      ACCRUED: 'Provisionado',
      PARTIALLY_SETTLED: 'Parcialmente baixado',
      SETTLED: 'Baixado',
      REVERSED: 'Revertido',
      CLOSED: 'Encerrado',
      SCHEDULED: 'Agendada',
      CANCELLED: 'Cancelada',
    },
    posting: {
      PENDING: 'Aguardando lançamento',
    },
    columns: {
      competency: 'Competência',
      costService: 'Serviço de custo',
      source: 'Origem',
      amount: 'Valor provisionado',
      basis: 'Base de cálculo',
      open: 'Em aberto',
      expectedPayment: 'Pagamento previsto',
      status: 'Situação',
      actions: 'Ações',
    },
    source: {
      invoice: 'Fatura',
      contract: 'Contrato',
      none: 'Sem fatura ou contrato',
    },
    actions: {
      view: 'Ver registro',
      close: 'Encerrar saldo',
      heal: 'Lançar pendentes',
      viewJournal: 'Ver lançamento',
      retry: 'Tentar de novo',
    },
    list: {
      viewsLabel: 'Visões das provisões de custo',
      tabs: {
        all: 'Em aberto',
        pending: 'Aguardando lançamento',
      },
      filters: {
        costService: 'Serviço de custo',
        allCostServices: 'Todos os serviços de custo',
        entity: 'Entidade',
        allEntities: 'Todas as entidades',
        month: 'Competência',
        clearMonth: 'Limpar competência',
      },
      count_one: '{{count}} provisão em aberto',
      count_other: '{{count}} provisões em aberto',
      limitNotice:
        'Mostrando as {{count}} provisões mais antigas. Filtre por serviço de custo ou competência para ver as demais.',
      serviceTotal: 'Em aberto: {{amount}}',
      monthSummary_one: '{{month}} · {{count}} provisão · {{amount}}',
      monthSummary_other: '{{month}} · {{count}} provisões · {{amount}}',
      empty: {
        title: 'Nenhum custo provisionado em aberto',
        description:
          'Quando uma fatura com serviço de custo vinculado for emitida, a provisão aparece aqui até ser baixada por uma conta a pagar.',
        filteredTitle: 'Nenhuma provisão para este filtro',
        filteredDescription: 'Mude o serviço de custo, a entidade ou a competência.',
        pendingTitle: 'Nenhuma provisão aguardando lançamento',
        pendingDescription: 'Todas as provisões em aberto já foram lançadas na contabilidade.',
      },
      loadError: 'Não foi possível carregar as provisões.',
      pendingNotice_one:
        '{{count}} provisão ainda não foi lançada na contabilidade. Corrija o motivo indicado e use "Lançar pendentes".',
      pendingNotice_other:
        '{{count}} provisões ainda não foram lançadas na contabilidade. Corrija o motivo indicado e use "Lançar pendentes".',
    },
    close: {
      title: 'Encerrar o saldo desta provisão?',
      description:
        'O fornecedor não vai cobrar o que está em aberto. O saldo sai do passivo e do custo no mesmo período, e o motivo fica registrado.',
      summaryService: 'Serviço de custo',
      summaryCompetency: 'Competência',
      summaryOutstanding: 'Saldo a encerrar',
      reasonLabel: 'Motivo do encerramento',
      reasonPlaceholder: 'Ex.: Fornecedor informou que não cobrará este período',
      footerNote: 'Informe o motivo para encerrar.',
      confirm: 'Encerrar saldo',
      success: 'Saldo encerrado: {{amount}}.',
      successJournal: 'Lançamento contábil gerado.',
      successNoJournal:
        'A provisão nunca foi lançada na contabilidade, então nenhum lançamento foi gerado.',
    },
    heal: {
      title: 'Lançar as provisões pendentes?',
      description:
        'Tenta lançar na contabilidade as provisões que ficaram aguardando por um motivo de configuração. As que ainda tiverem pendência continuam na lista.',
      confirm: 'Lançar pendentes',
      scopeAll: 'Serão consideradas as provisões de todas as entidades.',
      scopeEntity: 'Serão consideradas só as provisões da entidade {{name}}.',
      successNothing: 'Não havia provisões pendentes para lançar.',
      successSummary:
        'Lançadas: {{posted}}. Ainda pendentes: {{stillPending}}. Com falha: {{failed}}.',
      journalsPosted: 'Lançamentos contábeis gerados: {{count}}.',
      failuresTitle: 'Não foi possível lançar:',
      failureRow: 'Receita {{journalId}}: {{message}}',
    },
    aging: {
      title: 'Envelhecimento das provisões',
      subtitle:
        'Provisões em aberto há mais tempo que o limite do serviço de custo, por fornecedor. Elas podem estar já cobradas como despesa comum ou nunca virão.',
      asOfLabel: 'Posição em',
      clearAsOf: 'Usar a data de hoje',
      asOf: 'Posição em {{date}}',
      vendorFallback: 'Sem fornecedor',
      vendorUnavailable: 'Fornecedor indisponível',
      vendorOutstanding: 'Em aberto: {{amount}}',
      stale: 'Vencidas',
      pending: 'Aguardando lançamento',
      pendingHint:
        'Estas provisões nunca foram lançadas e já passaram do limite. Lance ou encerre cada uma.',
      columns: {
        costService: 'Serviço de custo',
        competency: 'Competência',
        expectedPayment: 'Pagamento previsto',
        daysOverdue: 'Dias de atraso',
        threshold: 'Limite',
        outstanding: 'Em aberto',
        actions: 'Ações',
      },
      days_one: '{{count}} dia',
      days_other: '{{count}} dias',
      expenseWarnings: 'Contas aprovadas como despesa enquanto havia provisão aberta',
      expenseWarning_one:
        'Conta {{number}} registrada em {{date}} com {{count}} linha como despesa.',
      expenseWarning_other:
        'Conta {{number}} registrada em {{date}} com {{count}} linhas como despesa.',
      expenseWarningNoNumber_one: 'Conta registrada em {{date}} com {{count}} linha como despesa.',
      expenseWarningNoNumber_other:
        'Conta registrada em {{date}} com {{count}} linhas como despesa.',
      empty: {
        title: 'Nenhuma provisão vencida',
        description:
          'Nenhuma provisão em aberto passou do limite de envelhecimento do seu serviço de custo.',
      },
      loadError: 'Não foi possível carregar o envelhecimento.',
      awaiting: {
        title: 'Antecipadas aguardando provisão',
        hint: 'Partes de contas lançadas no antecipado para uma provisão esperada que ainda não as baixou. Se a provisão nunca vier, encerre com um motivo: o saldo vai para despesa hoje.',
        columns: {
          bill: 'Conta',
          remaining: 'Ainda antecipado',
        },
        billFallback: 'Conta sem número',
        reserved: 'Reservado {{amount}}',
        close: 'Encerrar antecipação',
      },
      due: {
        title: 'Vencido, não reconhecido',
        hint: 'Meses do plano (Distribuir em N meses) que já venceram e ainda não foram reconhecidos: o custo não está no resultado nem no fluxo de caixa. Rode o reconhecimento de receita para lançá-los.',
        columns: {
          installment: 'Parcela',
          amount: 'Valor do plano',
        },
        installment: '{{index}} de {{count}}',
      },
    },
    closeAwaiting: {
      title: 'Encerrar esta antecipação?',
      description:
        'A provisão esperada não vai existir. O que ainda está no antecipado vai para despesa com um lançamento de hoje (débito em despesa, crédito no antecipado), e o motivo fica registrado. Se a provisão deste mês já estiver lançada, ela é baixada antes e só o restante vai para despesa.',
      summaryService: 'Serviço de custo',
      summaryCompetency: 'Competência',
      summaryBill: 'Conta',
      summaryRemaining: 'Saldo a encerrar',
      reasonLabel: 'Motivo do encerramento',
      reasonPlaceholder: 'Ex.: O fornecedor não vai prestar o serviço deste mês',
      footerNote: 'Informe o motivo para encerrar.',
      confirm: 'Encerrar antecipação',
      success: 'Antecipação encerrada: {{amount}} foi para despesa.',
      successConsumedAndClosed:
        'A provisão deste mês já estava lançada: {{consumed}} baixou a provisão e {{amount}} foi para despesa.',
      successConsumed:
        'A provisão deste mês já estava lançada e cobriu tudo: {{consumed}} baixou a provisão; nada foi para despesa.',
    },
    entry: {
      title: 'Registro da provisão',
      backToList: 'Voltar para custos provisionados',
      notFound: 'Provisão não encontrada.',
      loadError: 'Não foi possível carregar esta provisão.',
      readOnlyJournal: 'Sem permissão para abrir lançamentos contábeis.',
      summary: {
        title: 'Resumo',
        costService: 'Serviço de custo',
        entity: 'Entidade',
        vendor: 'Fornecedor',
        competency: 'Competência',
        expectedPayment: 'Pagamento previsto',
        trigger: 'Gatilho',
        baseType: 'Base',
        rate: 'Taxa',
        revenueBase: 'Base de receita',
        amount: 'Valor provisionado',
        reversed: 'Devolvido por reversão da receita',
        open: 'Em aberto',
        functionalAmount: 'Valor na moeda funcional',
        origin: 'Origem',
        noVendor: 'Sem fornecedor',
      },
      journals: {
        title: 'Lançamentos',
        revenue: 'Receita de origem',
        cost: 'Provisão do custo',
        reversal: 'Reversão',
        close: 'Encerramento',
        notPosted: 'Ainda não lançado',
      },
      matches: {
        title: 'Baixas por conta a pagar',
        description:
          'Cada linha de conta a pagar que pagou esta provisão. Desfazer uma baixa devolve o saldo e fica registrado aqui.',
        empty: 'Nenhuma conta a pagar baixou esta provisão ainda.',
        columns: {
          matchedAt: 'Quando',
          bill: 'Conta a pagar',
          applied: 'Valor baixado',
          openReduction: 'Redução do saldo',
          variance: 'Diferença',
          status: 'Situação',
          journal: 'Lançamento',
        },
        status: {
          LIVE: 'Ativa',
          UNDONE: 'Desfeita',
        },
        undone: 'Desfeita em {{date}}: {{reason}}',
        undoneNoReason: 'Desfeita em {{date}}',
        openBill: 'Abrir conta',
        undoJournal: 'Lançamento do desfazer',
      },
      events: {
        title: 'Reversões e encerramentos',
        empty: 'Esta provisão não foi revertida nem encerrada.',
        REVERSAL: 'Reversão da receita',
        REVERSAL_FULL: 'Reversão total da receita',
        REVERSAL_PARTIAL: 'Reversão parcial da receita',
        CLOSE: 'Encerramento do saldo',
        unitsLine: 'Valor: {{amount}}',
        reasonLine: 'Motivo: {{reason}}',
        reversalSource: 'Reversão da receita',
        notPosted: 'A provisão ainda não tinha sido lançada.',
      },
    },
    section: {
      title: 'Custos provisionados',
      description: 'Provisões de custo geradas por esta receita.',
      viewAll: 'Ver provisões em aberto',
      loadError: 'Não foi possível carregar as provisões desta receita.',
      none: {
        description:
          'Nenhuma provisão ainda. O custo é provisionado quando a receita é reconhecida: com a fatura aprovada e contabilizada, ou com o cronograma de receita reconhecido.',
        percentOfRevenue: '{{rate}} da receita',
        fixed: '{{amount}} fixo',
      },
    },
  },
};

export const costProvisionsEn = {
  costProvisions: {
    title: 'Provisioned costs',
    subtitle:
      'The cost you already recognized with the revenue and the vendor has not billed yet. Each bill that settles part of it reduces the open balance.',
    nav: {
      open: 'Open',
      aging: 'Aging',
    },
    status: {
      ACCRUED: 'Provisioned',
      PARTIALLY_SETTLED: 'Partly settled',
      SETTLED: 'Settled',
      REVERSED: 'Reversed',
      CLOSED: 'Closed out',
      SCHEDULED: 'Scheduled',
      CANCELLED: 'Cancelled',
    },
    posting: {
      PENDING: 'Waiting to post',
    },
    columns: {
      competency: 'Competency',
      costService: 'Cost service',
      source: 'Source',
      amount: 'Provisioned amount',
      basis: 'Basis',
      open: 'Open',
      expectedPayment: 'Expected payment',
      status: 'Status',
      actions: 'Actions',
    },
    source: {
      invoice: 'Invoice',
      contract: 'Contract',
      none: 'No invoice or contract',
    },
    actions: {
      view: 'View record',
      close: 'Close out balance',
      heal: 'Post pending',
      viewJournal: 'View journal',
      retry: 'Try again',
    },
    list: {
      viewsLabel: 'Cost provision views',
      tabs: {
        all: 'Open',
        pending: 'Waiting to post',
      },
      filters: {
        costService: 'Cost service',
        allCostServices: 'All cost services',
        entity: 'Entity',
        allEntities: 'All entities',
        month: 'Competency',
        clearMonth: 'Clear competency',
      },
      count_one: '{{count}} open provision',
      count_other: '{{count}} open provisions',
      limitNotice:
        'Showing the {{count}} oldest provisions. Filter by cost service or competency to see the rest.',
      serviceTotal: 'Open: {{amount}}',
      monthSummary_one: '{{month}} · {{count}} provision · {{amount}}',
      monthSummary_other: '{{month}} · {{count}} provisions · {{amount}}',
      empty: {
        title: 'No open provisioned costs',
        description:
          'When an invoice with a linked cost service is issued, its provision appears here until a bill settles it.',
        filteredTitle: 'No provisions for this filter',
        filteredDescription: 'Change the cost service, the entity or the competency.',
        pendingTitle: 'No provisions waiting to post',
        pendingDescription: 'Every open provision has already been posted to the books.',
      },
      loadError: 'Could not load the provisions.',
      pendingNotice_one:
        '{{count}} provision has not been posted to the books yet. Fix the reason shown and use "Post pending".',
      pendingNotice_other:
        '{{count}} provisions have not been posted to the books yet. Fix the reason shown and use "Post pending".',
    },
    close: {
      title: 'Close out the balance of this provision?',
      description:
        'The vendor will not bill what is still open. The balance leaves the liability and the cost in the same period, and the reason is recorded.',
      summaryService: 'Cost service',
      summaryCompetency: 'Competency',
      summaryOutstanding: 'Balance to close out',
      reasonLabel: 'Reason for the close-out',
      reasonPlaceholder: 'E.g. The vendor said it will not bill this period',
      footerNote: 'Enter the reason to close out.',
      confirm: 'Close out balance',
      success: 'Balance closed out: {{amount}}.',
      successJournal: 'Journal entry created.',
      successNoJournal: 'The provision was never posted to the books, so no journal was created.',
    },
    heal: {
      title: 'Post the pending provisions?',
      description:
        'Tries to post to the books the provisions that were left waiting for a setup reason. The ones that still have an issue stay in the list.',
      confirm: 'Post pending',
      scopeAll: 'The provisions of every entity will be considered.',
      scopeEntity: 'Only the provisions of the entity {{name}} will be considered.',
      successNothing: 'There were no pending provisions to post.',
      successSummary: 'Posted: {{posted}}. Still pending: {{stillPending}}. Failed: {{failed}}.',
      journalsPosted: 'Journal entries posted: {{count}}.',
      failuresTitle: 'Could not post:',
      failureRow: 'Revenue {{journalId}}: {{message}}',
    },
    aging: {
      title: 'Provision aging',
      subtitle:
        'Provisions open for longer than the limit of their cost service, by vendor. They may already be billed as plain expense, or may never be billed.',
      asOfLabel: 'As of',
      clearAsOf: 'Use today',
      asOf: 'As of {{date}}',
      vendorFallback: 'No vendor',
      vendorUnavailable: 'Vendor unavailable',
      vendorOutstanding: 'Open: {{amount}}',
      stale: 'Overdue',
      pending: 'Waiting to post',
      pendingHint:
        'These provisions were never posted and are past their limit. Post or close each one.',
      columns: {
        costService: 'Cost service',
        competency: 'Competency',
        expectedPayment: 'Expected payment',
        daysOverdue: 'Days overdue',
        threshold: 'Limit',
        outstanding: 'Open',
        actions: 'Actions',
      },
      days_one: '{{count}} day',
      days_other: '{{count}} days',
      expenseWarnings: 'Bills approved as expense while a provision was open',
      expenseWarning_one: 'Bill {{number}} recorded on {{date}} with {{count}} expense line.',
      expenseWarning_other: 'Bill {{number}} recorded on {{date}} with {{count}} expense lines.',
      expenseWarningNoNumber_one: 'Bill recorded on {{date}} with {{count}} expense line.',
      expenseWarningNoNumber_other: 'Bill recorded on {{date}} with {{count}} expense lines.',
      empty: {
        title: 'No overdue provisions',
        description: 'No open provision is past the aging limit of its cost service.',
      },
      loadError: 'Could not load the aging report.',
      awaiting: {
        title: 'Prepaid parts awaiting their provision',
        hint: 'Bill parts booked as prepaid for an expected provision that has not settled them. If the provision will never come, close them with a reason: the balance goes to expense today.',
        columns: {
          bill: 'Bill',
          remaining: 'Still prepaid',
        },
        billFallback: 'Bill without number',
        reserved: 'Reserved {{amount}}',
        close: 'Close prepayment',
      },
      due: {
        title: 'Due, not recognized',
        hint: 'Plan months (Spread over N months) that are already due and were not recognized yet: the cost is in neither the P&L nor the cash flow. Run revenue recognition in batch to post them.',
        columns: {
          installment: 'Installment',
          amount: 'Plan amount',
        },
        installment: '{{index}} of {{count}}',
      },
    },
    closeAwaiting: {
      title: 'Close this prepayment?',
      description:
        'The expected provision will not exist. What is still prepaid goes to expense with an entry dated today (debit expense, credit prepaid), and the reason is recorded. If the provision for this month is already posted, it is relieved first and only the rest goes to expense.',
      summaryService: 'Cost service',
      summaryCompetency: 'Competency',
      summaryBill: 'Bill',
      summaryRemaining: 'Balance to close',
      reasonLabel: 'Reason for closing',
      reasonPlaceholder: 'E.g. The vendor will not provide the service this month',
      footerNote: 'Enter the reason to close.',
      confirm: 'Close prepayment',
      success: 'Prepayment closed: {{amount}} went to expense.',
      successConsumedAndClosed:
        "This month's provision was already posted: {{consumed}} relieved the provision and {{amount}} went to expense.",
      successConsumed:
        "This month's provision was already posted and covered it all: {{consumed}} relieved the provision; nothing went to expense.",
    },
    entry: {
      title: 'Provision record',
      backToList: 'Back to provisioned costs',
      notFound: 'Provision not found.',
      loadError: 'Could not load this provision.',
      readOnlyJournal: 'You cannot open journal entries.',
      summary: {
        title: 'Summary',
        costService: 'Cost service',
        entity: 'Entity',
        vendor: 'Vendor',
        competency: 'Competency',
        expectedPayment: 'Expected payment',
        trigger: 'Trigger',
        baseType: 'Basis',
        rate: 'Rate',
        revenueBase: 'Revenue basis',
        amount: 'Provisioned amount',
        reversed: 'Given back by revenue reversal',
        open: 'Open',
        functionalAmount: 'Amount in functional currency',
        origin: 'Source',
        noVendor: 'No vendor',
      },
      journals: {
        title: 'Journal entries',
        revenue: 'Source revenue',
        cost: 'Cost provision',
        reversal: 'Reversal',
        close: 'Close-out',
        notPosted: 'Not posted yet',
      },
      matches: {
        title: 'Settlements by bills',
        description:
          'Each bill line that settled this provision. Undoing a settlement gives the balance back and stays recorded here.',
        empty: 'No bill has settled this provision yet.',
        columns: {
          matchedAt: 'When',
          bill: 'Bill',
          applied: 'Amount settled',
          openReduction: 'Balance reduction',
          variance: 'Variance',
          status: 'Status',
          journal: 'Journal entry',
        },
        status: {
          LIVE: 'Active',
          UNDONE: 'Undone',
        },
        undone: 'Undone on {{date}}: {{reason}}',
        undoneNoReason: 'Undone on {{date}}',
        openBill: 'Open bill',
        undoJournal: 'Undo journal entry',
      },
      events: {
        title: 'Reversals and close-outs',
        empty: 'This provision has not been reversed or closed out.',
        REVERSAL: 'Revenue reversal',
        REVERSAL_FULL: 'Full revenue reversal',
        REVERSAL_PARTIAL: 'Partial revenue reversal',
        CLOSE: 'Balance close-out',
        unitsLine: 'Amount: {{amount}}',
        reasonLine: 'Reason: {{reason}}',
        reversalSource: 'Revenue reversal',
        notPosted: 'The provision had not been posted yet.',
      },
    },
    section: {
      title: 'Provisioned costs',
      description: 'Cost provisions created by this revenue.',
      viewAll: 'View open provisions',
      loadError: 'Could not load the provisions of this revenue.',
      none: {
        description:
          'No provision yet. The cost is provisioned when the revenue is recognized: once the invoice is approved and posted, or the revenue schedule is recognized.',
        percentOfRevenue: '{{rate}} of revenue',
        fixed: '{{amount}} fixed',
      },
    },
  },
};
